/* ═══════════════════════════════════════════════════════════════
   paginate — where the pages break (Pages view, display only).

   Pure: given the measured top-level blocks of a continuous sheet (natural
   positions, as if no page breaks were drawn) and the usable page height,
   return where each page starts. Nothing here touches the document, the
   export or co-editing — it decides what the paper DRAWS.

   Hand-built by Josh's express approval (2026-11-02, "yes build the page
   view option. I authorize the hand built for just that."). Alternative
   considered: tiptap-pagination-plus / tiptap-extension-pagination (young,
   no zone bands, unverified with collaboration cursors).

   Rules (R1):
   - A heading is never the last block on a page — keep-with-next pulls it
     to the next page with what follows it.
   - A table never splits: it moves whole to the next page. One taller than
     a page cannot move anywhere better; it OVERFLOWS and is reported, never
     clipped.
   - A paragraph with a line metric splits at a LINE boundary (never
     mid-line); widow/orphan control keeps two lines on each side when the
     forma asks for it. Without a line metric it is unsplittable.
   - The forma's intentionally-blank page (Section End ▸ blank page, the
     same condition the Word engine applies) adds one blank page at the end.

   Word lays out with its own metrics, so a break can land a line off.
   ═══════════════════════════════════════════════════════════════ */

import type { StandardConfig } from '../../../shared/types';

export type PageBlockKind = 'heading' | 'table' | 'para' | 'block' | 'tail';

/** One top-level block, measured in natural (break-free) flow coordinates.
 *  Origin = the top of page 1's text area. Any unit, as long as every
 *  number (and the page height) uses the same one. */
export interface BlockMeasure {
  id: string;
  top: number;
  height: number;
  kind: PageBlockKind;
  /** Line pitch of a paragraph. Absent → the paragraph is unsplittable. */
  lineHeight?: number;
  /** The block's own space-before. When a page starts with this block the
   *  space travels with it (the drawn page holds it). */
  spaceBefore?: number;
}

export type BlankPageCondition = 'never' | 'always' | 'whenOdd' | 'whenEven';

export interface PaginateRules {
  /** Word's widow/orphan control: two lines minimum each side of a split. */
  widowOrphan?: boolean;
  /** The intentionally-blank-page rule (null/undefined = off). */
  blankPage?: BlankPageCondition | null;
}

export interface PageBreak {
  /** The block the new page starts with (or continues, when split). Equal
   *  to the block count → the page starts after the last block (the tail). */
  beforeBlockIndex: number;
  /** The number of the page that starts here. */
  pageNum: number;
  /** Split inside the block: offset from the block's top, on a line
   *  boundary. Absent → the page starts at the block's top. */
  splitOffset?: number;
  /** The page before this break holds a block taller than a page. */
  overflow?: boolean;
}

export interface PaginateResult {
  breaks: PageBreak[];
  /** Every page, the blank page included. */
  numPages: number;
  /** Indexes of blocks taller than a page (drawn overflowing, not clipped). */
  overflows: number[];
  /** The intentionally-blank page fires — it is the last page. */
  blankPage: boolean;
}

export const EMPTY_PAGINATION: PaginateResult = Object.freeze({
  breaks: [],
  numPages: 1,
  overflows: [],
  blankPage: false,
}) as PaginateResult;

/** The forma's blank-page condition, read exactly as the Word engine reads
 *  it (word-engine.ts injectBlankPage / settleBlankPageParity). */
export function blankPageRule(forma: StandardConfig | null | undefined): BlankPageCondition {
  const sp = (forma?.special || {}) as { blankPageEnabled?: boolean; blankPageCondition?: string };
  if (!sp.blankPageEnabled) return 'never';
  const c = sp.blankPageCondition || 'always';
  return c === 'whenOdd' || c === 'whenEven' || c === 'never' ? c : 'always';
}

/** The last page that carries text (the intentionally-blank page, when it
 *  fires, follows it). */
export function lastTextPage(p: { numPages: number; blankPage?: unknown }): number {
  return Math.max(1, p.numPages - (p.blankPage ? 1 : 0));
}

/** Does the blank page fire for a document of `pages` pages? */
export function blankPageFires(cond: BlankPageCondition | null | undefined, pages: number) {
  if (!cond || cond === 'never') return false;
  if (cond === 'always') return true;
  const odd = pages % 2 !== 0;
  return cond === 'whenOdd' ? odd : !odd;
}

/** The engine's keep-with-next classification (word-engine.ts
 *  applyKeepNextMapping): a paragraph is a HEADING when what follows it is
 *  deeper — a deeper level, or a table. `keepLines` levels never split. */
export function classifyBlocks(
  items: Array<{ type: 'text' | 'table' | 'other'; level?: number; keepLines?: boolean }>
): PageBlockKind[] {
  return items.map((it, i) => {
    if (it.type === 'table') return 'table';
    if (it.type !== 'text') return 'block';
    const next = items[i + 1];
    const deeper =
      !!next &&
      (next.type === 'table' || (next.type === 'text' && (next.level ?? 0) > (it.level ?? 0)));
    if (deeper) return 'heading';
    return it.keepLines ? 'block' : 'para';
  });
}

export function paginate(
  blocks: BlockMeasure[],
  pageHeight: number,
  rules: PaginateRules = {}
): PaginateResult {
  const H = pageHeight;
  if (!(H > 0) || blocks.length === 0) {
    const numPages = 1 + (blankPageFires(rules.blankPage, 1) ? 1 : 0);
    return { breaks: [], numPages, overflows: [], blankPage: numPages > 1 };
  }
  // Tolerance scales with the page so zoom never changes a decision.
  const eps = H * 1e-4;
  const breaks: PageBreak[] = [];
  const overflows: number[] = [];
  let page = 1;
  let pageStart = 0;
  let firstOnPage = 0;
  let pendingOverflow = false;

  const startPageAt = (index: number, y: number, splitOffset?: number) => {
    page += 1;
    const brk: PageBreak = { beforeBlockIndex: index, pageNum: page };
    if (splitOffset != null) brk.splitOffset = splitOffset;
    if (pendingOverflow) brk.overflow = true;
    pendingOverflow = false;
    breaks.push(brk);
    pageStart = y;
    firstOnPage = index;
  };

  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    const top = b.top;
    const bottom = b.top + b.height;

    while (bottom > pageStart + H + eps) {
      const limit = pageStart + H;
      const isFirst = i === firstOnPage;
      const lh = b.kind === 'para' && b.lineHeight && b.lineHeight > 0 ? b.lineHeight : 0;

      if (lh) {
        const segTop = Math.max(top, pageStart);
        const totalLines = Math.max(1, Math.round(b.height / lh));
        const placed = Math.max(0, Math.round((segTop - top) / lh));
        const remaining = totalLines - placed;
        let fit = Math.floor((limit - segTop + eps) / lh);
        if (fit >= remaining) break; // rounding — it fits after all
        if (rules.widowOrphan && remaining >= 2) {
          // Widow: never leave a single line for the next page.
          fit = Math.min(fit, remaining - 2);
          // Orphan: never strand a paragraph's first line alone.
          if (placed === 0 && fit < 2) fit = 0;
          // A paragraph that starts the page must place something.
          if (fit < 1 && isFirst)
            fit = Math.min(remaining - 1, Math.floor((limit - segTop + eps) / lh));
        }
        if (fit >= 1 && fit < remaining) {
          const off = (placed + fit) * lh;
          startPageAt(i, top + off, off);
          continue;
        }
      }

      if (isFirst) {
        // Starts the page and still does not fit: nothing to gain by moving
        // it. It overflows — reported, drawn whole.
        overflows.push(i);
        const span = Math.max(1, Math.ceil((bottom - pageStart - eps) / H));
        page += span - 1;
        pageStart = bottom;
        pendingOverflow = true;
        // The next block (if any) opens a fresh page.
        if (i + 1 < blocks.length) {
          const next = blocks[i + 1];
          startPageAt(i + 1, next.top - (next.spaceBefore ?? 0));
        } else {
          pendingOverflow = false;
        }
        break;
      }

      // Move it to the next page — and pull any heading above it along
      // (keep-with-next), as long as the page keeps at least one block.
      let j = i;
      while (j - 1 > firstOnPage && blocks[j - 1].kind === 'heading') j--;
      const moved = blocks[j];
      startPageAt(j, moved.top - (moved.spaceBefore ?? 0));
      i = j - 1; // re-flow from the moved block
      break;
    }
  }

  const blank = blankPageFires(rules.blankPage, page);
  return { breaks, numPages: page + (blank ? 1 : 0), overflows, blankPage: blank };
}
